import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pill, Sparkles, ShieldCheck, Check, ArrowRight, Tag, Package2, Plus } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

/**
 * Reusable storefront for the public Pharmaceuticals & Nutraceuticals pages.
 * Props: kind = "pharmaceutical" | "nutraceutical"
 */
const FALLBACK = {
  pharmaceutical: {
    eyebrow: "PHARMACEUTICALS & PHARMACY",
    title: "Clinically reviewed medicines, sourced from verified partners.",
    subtitle: "",
    info_lines: [],
  },
  nutraceutical: {
    eyebrow: "NUTRACEUTICALS",
    title: "Evidence-led supplements & nutrition, hand-picked by specialists.",
    subtitle: "",
    info_lines: [],
  },
};

const HeroIcon = { pharmaceutical: Pill, nutraceutical: Sparkles };

function priceLine(p) {
  const price = Number(p.price || 0);
  const mrp = Number(p.mrp || 0);
  if (!price && !mrp) return null;
  if (mrp && mrp > price) {
    const off = Math.round(((mrp - price) / mrp) * 100);
    return (
      <span className="store-price">
        <b>₹{price.toLocaleString("en-IN")}</b>
        <span className="store-mrp">₹{mrp.toLocaleString("en-IN")}</span>
        {off > 0 && <span className="store-off">−{off}%</span>}
      </span>
    );
  }
  return <span className="store-price"><b>₹{(price || mrp).toLocaleString("en-IN")}</b></span>;
}

export default function Storefront({ kind }) {
  const [content, setContent] = useState(FALLBACK[kind]);
  const [data, setData] = useState({ categories: [], products: [] });
  const [activeCat, setActiveCat] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const { add } = useCart();
  const Icon = HeroIcon[kind];

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/public/storefront/content", { params: { kind } })
        .then((r) => setContent({ ...FALLBACK[kind], ...(r.data?.data || {}) }))
        .catch(() => {}),
      api.get("/public/products", { params: { kind } })
        .then((r) => setData({ categories: r.data?.categories || [], products: r.data?.products || [] }))
        .catch(() => setData({ categories: [], products: [] })),
    ]).finally(() => setLoading(false));
  }, [kind]);

  const filtered = activeCat
    ? data.products.filter((p) => p.category_id === activeCat)
    : data.products;

  return (
    <div className="store-wrap" data-testid={`store-${kind}`}>
      <section className={`store-hero ${kind}`}>
        <div className="store-hero-in">
          <span className="b2b-eyebrow">{content.eyebrow}</span>
          <h1 className="b2b-h1">{content.title}</h1>
          {content.subtitle && <p className="b2b-sub">{content.subtitle}</p>}
          {content.info_lines?.length > 0 && (
            <ul className="store-info">
              {content.info_lines.map((l, i) => (
                <li key={i}><Check size={14} /> {l}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="store-hero-ic" aria-hidden><Icon size={88} /></div>
      </section>

      <section className="store-body">
        {/* category filter */}
        {data.categories.length > 0 && (
          <div className="store-cats" data-testid="store-categories">
            <button
              className={`store-cat ${!activeCat ? "on" : ""}`}
              onClick={() => setActiveCat("")}
              data-testid="store-cat-all"
            >All</button>
            {data.categories.map((c) => (
              <button
                key={c.id}
                className={`store-cat ${activeCat === c.id ? "on" : ""}`}
                onClick={() => setActiveCat(c.id)}
                data-testid={`store-cat-${c.id}`}
              >{c.name}</button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="lead">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="store-empty" data-testid="store-empty">
            <Package2 size={42} />
            <h3>Products will appear here soon.</h3>
            <p>
              We're onboarding brand partners. Run a brand?{" "}
              <a onClick={() => nav("/b2b#contact")} className="store-link">Partner with us →</a>
            </p>
          </div>
        ) : (
          <div className="store-grid" data-testid="store-grid">
            {filtered.map((p) => (
              <div className="store-card" key={p.id} data-testid={`store-product-${p.id}`}>
                {p.featured && <span className="store-badge"><Tag size={11} /> Featured</span>}
                {p.rx_required && <span className="store-rx">Rx</span>}
                <div className="store-card-img">
                  {p.image_url
                    ? <img src={p.image_url} alt={p.name} loading="lazy" />
                    : <div className="store-card-imgph"><Icon size={36} /></div>}
                </div>
                <div className="store-card-body">
                  {p.category_name && <small className="store-cat-chip">{p.category_name}</small>}
                  <h3>{p.name}</h3>
                  {p.tagline && <p className="store-tagline">{p.tagline}</p>}
                  <div className="store-card-foot">
                    {priceLine(p)}
                    {p.partner_name && <small className="store-partner"><ShieldCheck size={11} /> {p.partner_name}</small>}
                  </div>
                  {p.in_stock === false ? (
                    <span className="store-oos">Out of stock</span>
                  ) : (
                    <button
                      className="store-add-btn"
                      onClick={() => add(p, 1)}
                      data-testid={`store-add-${p.id}`}
                    >
                      <Plus size={14} /> Add to cart
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="store-partner-cta">
        <div className="store-pcta-in">
          <div>
            <h2 className="b2b-h2">Sell on this storefront.</h2>
            <p>Verified brands list their products beside our specialists. Reach the patients who already trust us.</p>
          </div>
          <a onClick={() => nav("/b2b#contact")} className="pg-btn pg-btn-ink" data-testid="store-cta-partner">
            Partner with us <ArrowRight size={15} />
          </a>
        </div>
      </section>
    </div>
  );
}
